import { a as FRAME_RECORD_HEADER_SIZE, c as readFrameLog, d as readZip, f as crc32, i as parseManifest, l as Sha256, n as readSession, o as FrameLogWriter, p as crc32Update, r as SESSION_FORMAT_VERSION, s as countFrames, t as Session, u as sha256Hex } from "./reader-Cd74Mhmv.js";
import { ATHENA_CONTROL_CHARACTERISTIC, MICROSECONDS_PER_MILLISECOND, MuseError, sameCharacteristicUuid, systemTimeSource, toCanonicalCharacteristicUuid } from "@musejs/core";
//#region src/zip/writer.ts
const LOCAL_SIGNATURE = 67324752;
const CENTRAL_SIGNATURE = 33639248;
const EOCD_SIGNATURE = 101010256;
/** Bit 11: file names are UTF-8. */
const FLAG_UTF8 = 2048;
const METHOD_STORE = 0;
const VERSION = 20;
const MAX_SIZE = 4294967295;
/** Entry counts and name lengths are 16-bit fields; a larger value silently wraps. */
const MAX_UINT16 = 65535;
/** Fixed DOS timestamp (1980-01-01 00:00:00), for byte-for-byte determinism. */
const DOS_TIME = 0;
const DOS_DATE = 33;
function writeZip(entries) {
	const encoder = new TextEncoder();
	if (entries.length > MAX_UINT16) throw new MuseError("MALFORMED_PACKET", `${entries.length} entries; this writer does not emit ZIP64 (limit ${MAX_UINT16})`);
	const names = /* @__PURE__ */ new Set();
	for (const entry of entries) {
		if (names.has(entry.name)) throw new MuseError("MALFORMED_PACKET", `Duplicate entry name "${entry.name}"`);
		names.add(entry.name);
	}
	const prepared = entries.map((entry) => {
		const name = encoder.encode(entry.name);
		if (name.length > MAX_UINT16) throw new MuseError("MALFORMED_PACKET", `Entry name is ${name.length} bytes; the limit is ${MAX_UINT16}`);
		let size = 0;
		let crc = 0;
		for (const chunk of entry.chunks) {
			size += chunk.length;
			crc = crc32Update(crc, chunk);
		}
		if (size > MAX_SIZE) throw new MuseError("MALFORMED_PACKET", `Entry "${entry.name}" is ${size} bytes; this writer does not emit ZIP64 (limit ${MAX_SIZE})`, { details: {
			entry: entry.name,
			size
		} });
		return {
			entry,
			name,
			size,
			crc
		};
	});
	const total = prepared.reduce((n, p) => n + 30 + p.name.length + p.size, 0) + prepared.reduce((n, p) => n + 46 + p.name.length, 0) + 22;
	if (total > MAX_SIZE) throw new MuseError("MALFORMED_PACKET", `Archive would be ${total} bytes; ZIP64 is not supported`);
	const out = new Uint8Array(total);
	const view = new DataView(out.buffer);
	let offset = 0;
	const localOffsets = [];
	for (const { entry, name, size, crc } of prepared) {
		localOffsets.push(offset);
		view.setUint32(offset, LOCAL_SIGNATURE, true);
		view.setUint16(offset + 4, VERSION, true);
		view.setUint16(offset + 6, FLAG_UTF8, true);
		view.setUint16(offset + 8, METHOD_STORE, true);
		view.setUint16(offset + 10, DOS_TIME, true);
		view.setUint16(offset + 12, DOS_DATE, true);
		view.setUint32(offset + 14, crc, true);
		view.setUint32(offset + 18, size, true);
		view.setUint32(offset + 22, size, true);
		view.setUint16(offset + 26, name.length, true);
		view.setUint16(offset + 28, 0, true);
		out.set(name, offset + 30);
		offset += 30 + name.length;
		for (const chunk of entry.chunks) {
			out.set(chunk, offset);
			offset += chunk.length;
		}
	}
	const centralStart = offset;
	prepared.forEach(({ name, size, crc }, i) => {
		view.setUint32(offset, CENTRAL_SIGNATURE, true);
		view.setUint16(offset + 4, VERSION, true);
		view.setUint16(offset + 6, VERSION, true);
		view.setUint16(offset + 8, FLAG_UTF8, true);
		view.setUint16(offset + 10, METHOD_STORE, true);
		view.setUint16(offset + 12, DOS_TIME, true);
		view.setUint16(offset + 14, DOS_DATE, true);
		view.setUint32(offset + 16, crc, true);
		view.setUint32(offset + 20, size, true);
		view.setUint32(offset + 24, size, true);
		view.setUint16(offset + 28, name.length, true);
		view.setUint16(offset + 30, 0, true);
		view.setUint16(offset + 32, 0, true);
		view.setUint16(offset + 34, 0, true);
		view.setUint16(offset + 36, 0, true);
		view.setUint32(offset + 38, 0, true);
		view.setUint32(offset + 42, localOffsets[i], true);
		out.set(name, offset + 46);
		offset += 46 + name.length;
	});
	const centralDirectorySize = offset - centralStart;
	view.setUint32(offset, EOCD_SIGNATURE, true);
	view.setUint16(offset + 4, 0, true);
	view.setUint16(offset + 6, 0, true);
	view.setUint16(offset + 8, prepared.length, true);
	view.setUint16(offset + 10, prepared.length, true);
	view.setUint32(offset + 12, centralDirectorySize, true);
	view.setUint32(offset + 16, centralStart, true);
	view.setUint16(offset + 20, 0, true);
	return out;
}
//#endregion
//#region src/session/writer.ts
const DEFAULT_DEVICE = {
	id: "unknown",
	name: null,
	model: null,
	protocol: "unknown",
	firmware: null,
	mtu: null
};
/**
* Builds a `.musez` session.
*
* The writer records **raw notifications only**. Decoded signals are not stored
* in this format version: they are a function of the raw frames plus a decoder
* version, and storing a derived copy invites the two to disagree. Regenerating
* them costs milliseconds.
*/
var SessionWriter = class {
	#frames = new FrameLogWriter();
	#characteristics = [];
	#characteristicIndex = /* @__PURE__ */ new Map();
	/** One JSON line per event, serialised when it was added. */
	#events = [];
	#time;
	#options;
	#device;
	#preset = {
		requested: null,
		observedSignals: []
	};
	#startedAt = null;
	#endedAt = null;
	#finished = false;
	constructor(options) {
		if (options.extra !== void 0) serialisable(options.extra, "The session's `extra` metadata");
		this.#options = options;
		this.#time = options.timeSource ?? systemTimeSource;
		this.#device = {
			...DEFAULT_DEVICE,
			...options.device
		};
	}
	get frameCount() {
		return this.#frames.frameCount;
	}
	get eventCount() {
		return this.#events.length;
	}
	/** Records one notification exactly as received. Copies the payload. */
	addFrame(characteristic, payload, arrivalTime) {
		this.#assertOpen();
		if (this.#startedAt === null) this.#startedAt = arrivalTime;
		this.#endedAt = arrivalTime;
		this.#frames.append({
			characteristicIndex: this.#indexFor(characteristic),
			arrivalTime,
			payload: payload.slice()
		});
	}
	addEvent(event) {
		this.#assertOpen();
		this.#events.push(serialisable(event, `Marker "${event.label}"`));
	}
	/**
	* Marks a point in time.
	*
	* `latencyUncertaintyUs` defaults to 10 ms, not 0: a marker stamped on the
	* host has no hardware relationship to the headband's clock, and recording
	* zero would assert a precision this setup cannot deliver.
	*/
	mark(label, data) {
		const event = {
			label,
			timestamp: this.#time.now(),
			source: "host",
			latencyUncertaintyUs: 1e4,
			...data === void 0 ? {} : { data }
		};
		this.addEvent(event);
		return event;
	}
	describeDevice(device) {
		this.#assertOpen();
		this.#device = {
			...this.#device,
			...device
		};
	}
	describePreset(preset) {
		this.#assertOpen();
		this.#preset = {
			...this.#preset,
			...preset
		};
	}
	/**
	* Serialises the session. The writer cannot be used afterwards.
	*
	* If serialising fails, the writer stays open and nothing is lost: it only
	* closes once the archive exists.
	*/
	finish() {
		this.#assertOpen();
		const chunks = this.#frames.chunks();
		const manifest = {
			formatVersion: 1,
			library: this.#options.library ?? {
				name: "@musejs/recording",
				version: "0.0.0"
			},
			transport: this.#options.transport,
			device: this.#device,
			preset: this.#preset,
			characteristics: [...this.#characteristics],
			startedAt: this.#startedAt ?? this.#time.now(),
			endedAt: this.#endedAt ?? this.#time.now(),
			integrity: {
				rawFrameCount: this.#frames.frameCount,
				rawByteLength: this.#frames.byteLength,
				rawSha256: sha256Hex(chunks)
			},
			...this.#options.subject === void 0 ? {} : { subject: this.#options.subject },
			...this.#options.session === void 0 ? {} : { session: this.#options.session },
			...this.#options.task === void 0 ? {} : { task: this.#options.task },
			...this.#options.notes === void 0 ? {} : { notes: this.#options.notes },
			...this.#options.extra === void 0 ? {} : { extra: this.#options.extra }
		};
		const encoder = new TextEncoder();
		const archive = writeZip([
			{
				name: "manifest.json",
				chunks: [encoder.encode(JSON.stringify(manifest, null, 2) + "\n")]
			},
			{
				name: "raw/frames.bin",
				chunks
			},
			{
				name: "raw/frames.idx",
				chunks: [this.#frames.index()]
			},
			{
				name: "events.jsonl",
				chunks: [encoder.encode(this.#events.join("\n") + (this.#events.length > 0 ? "\n" : ""))]
			}
		]);
		this.#finished = true;
		return archive;
	}
	#indexFor(characteristic) {
		const existing = this.#characteristicIndex.get(characteristic);
		if (existing !== void 0) return existing;
		const index = this.#characteristics.length;
		this.#characteristics.push(characteristic);
		this.#characteristicIndex.set(characteristic, index);
		return index;
	}
	#assertOpen() {
		if (this.#finished) throw new MuseError("MALFORMED_PACKET", "SessionWriter has already been finished");
	}
};
/** JSON-serialises `value`, or throws a `MuseError` naming what could not be written. */
function serialisable(value, what) {
	try {
		return JSON.stringify(value);
	} catch (cause) {
		throw new MuseError("MALFORMED_PACKET", `${what} cannot be written as JSON`, { cause });
	}
}
//#endregion
//#region src/replay/transport.ts
/**
* How many frames `"instant"` replay delivers between yields to the event loop.
*
* A microtask yield alone never lets timers or I/O run, so `loop: true` once
* wedged the host permanently. A macrotask every few dozen frames keeps replay
* fast and the event loop alive.
*/
const FRAMES_PER_MACROTASK = 64;
const yieldToEventLoop = () => new Promise((resolve) => setTimeout(resolve, 0));
/**
* Replays a recorded session as a `Transport`.
*
* This is not a mock. It satisfies the same interface as the Web Bluetooth and
* Node backends, so a test drives the production decode path end to end and a
* bug reproduced from a recording is reproduced in the real code.
*
* Notifications carry their **recorded** arrival timestamps regardless of replay
* speed. Re-stamping them with the current clock would make replay non-
* deterministic and would silently invalidate every timing analysis.
*
* Writes are accepted and logged, never acted upon: a recording cannot answer a
* command it never received.
*/
function replayTransport(session, options = {}) {
	return new ReplayTransport(session, options);
}
var ReplayTransport = class {
	name = "replay";
	#session;
	#options;
	#deviceId;
	#connection = null;
	constructor(session, options = {}) {
		const speed = options.speed ?? "instant";
		if (typeof speed === "number" && !(speed > 0)) throw new MuseError("MALFORMED_PACKET", `Replay speed must be a positive multiplier, got ${speed}`, { details: { speed } });
		this.#session = session;
		this.#options = options;
		this.#deviceId = options.deviceId ?? session.manifest.device.id;
	}
	/** Writes seen by the most recent connection. */
	get writes() {
		return this.#connection?.writes ?? [];
	}
	async *scan(options = {}) {
		if (options.signal?.aborted === true) return;
		const device = this.#session.manifest.device;
		yield {
			id: this.#deviceId,
			name: device.name,
			rssi: null,
			discoveredAt: this.#session.manifest.startedAt
		};
	}
	async open(deviceId, options = {}) {
		options.signal?.throwIfAborted();
		if (deviceId !== this.#deviceId) throw new MuseError("DEVICE_NOT_FOUND", `This recording is of device ${this.#deviceId}, not ${deviceId}`, { details: {
			requested: deviceId,
			recorded: this.#deviceId
		} });
		this.#connection = new ReplayConnection(this.#session, this.#options, this.#deviceId);
		return this.#connection;
	}
};
var ReplayConnection = class {
	id;
	mtu;
	closed;
	writes = [];
	#session;
	#options;
	#listeners = /* @__PURE__ */ new Map();
	/** Characteristics the recording holds data for, canonicalised. */
	#recorded;
	/** Manifest spelling → canonical form, so delivery matches however it was subscribed. */
	#canonical = /* @__PURE__ */ new Map();
	#settle;
	#done = false;
	#pumping = false;
	constructor(session, options, deviceId) {
		this.#session = session;
		this.#options = options;
		this.id = deviceId;
		this.mtu = session.manifest.device.mtu;
		this.#recorded = new Set(session.manifest.characteristics.map((c) => this.#canonicalFor(c)));
		this.closed = new Promise((resolve) => {
			this.#settle = resolve;
		});
	}
	async subscribe(characteristic, listener) {
		if (this.#done) throw new MuseError("CONNECTION_LOST", "Replay connection is closed");
		const key = this.#canonicalFor(characteristic);
		if (!this.#recorded.has(key) && !sameCharacteristicUuid(key, ATHENA_CONTROL_CHARACTERISTIC)) throw new MuseError("UNSUPPORTED_DEVICE", `This recording holds no data on ${characteristic}`, { details: {
			characteristic,
			recorded: [...this.#recorded]
		} });
		let set = this.#listeners.get(key);
		if (set === void 0) {
			set = /* @__PURE__ */ new Set();
			this.#listeners.set(key, set);
		}
		set.add(listener);
		this.#startPump();
		return () => {
			set.delete(listener);
		};
	}
	async write(characteristic, data, _options = {}) {
		if (this.#done) throw new MuseError("CONNECTION_LOST", "Replay connection is closed");
		this.writes.push({
			characteristic,
			data: data.slice(),
			at: this.#session.manifest.startedAt
		});
	}
	async close() {
		this.#finish({ kind: "requested" });
	}
	#startPump() {
		if (this.#pumping || this.#done) return;
		this.#pumping = true;
		this.#pump().catch((cause) => {
			this.#finish({
				kind: "error",
				error: MuseError.wrap(cause, "MALFORMED_PACKET", "Replay failed")
			});
		});
	}
	async #pump() {
		const speed = this.#options.speed ?? "instant";
		let delivered = 0;
		do {
			let anchor = null;
			let pass = 0;
			for (const frame of this.#session.frames()) {
				if (this.#done) return;
				anchor = await this.#pace(frame, anchor, speed);
				if (this.#done) return;
				this.#deliver(frame);
				pass++;
				if (speed === "instant" && ++delivered % FRAMES_PER_MACROTASK === 0) await yieldToEventLoop();
			}
			if (pass === 0) break;
		} while (this.#options.loop === true && !this.#done);
		this.#finish({ kind: "ended" });
	}
	/**
	* Waits until `frame` is due, and returns the anchor the pass is paced from.
	*
	* Every frame is scheduled against the start of the pass, not against the
	* frame before it. Sleeping for each recorded gap in turn adds every timer's
	* overshoot to the next, and a browser clamps short timers to 4 ms: [M] that
	* way Chromium took 116.6 s to replay the 62.1 s playground capture.
	*/
	async #pace(frame, anchor, speed) {
		if (speed === "instant") {
			await Promise.resolve();
			return null;
		}
		const start = anchor ?? {
			arrivalTime: frame.arrivalTime,
			wallTime: systemTimeSource.now()
		};
		const multiplier = speed === "realtime" ? 1 : speed;
		const delayMs = (start.wallTime + (frame.arrivalTime - start.arrivalTime) / multiplier - systemTimeSource.now()) / 1e3;
		if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs));
		return start;
	}
	#canonicalFor(characteristic) {
		let canonical = this.#canonical.get(characteristic);
		if (canonical === void 0) {
			canonical = toCanonicalCharacteristicUuid(characteristic);
			this.#canonical.set(characteristic, canonical);
		}
		return canonical;
	}
	#deliver(frame) {
		const listeners = this.#listeners.get(this.#canonicalFor(frame.characteristic));
		if (listeners === void 0 || listeners.size === 0) return;
		for (const listener of listeners) listener(frame.payload, frame.arrivalTime);
	}
	#finish(reason) {
		if (this.#done) return;
		this.#done = true;
		this.#listeners.clear();
		this.#settle(reason);
	}
};
//#endregion
//#region src/import/openmuse.ts
/**
* Converts an OpenMuse text recording into a `.musez` session.
*
* Upstream format, one notification per line:
* ```
* <ISO-8601 timestamp>\t<characteristic UUID>\t<lowercase hex payload>
* ```
*
* The import is lossless with one caveat worth stating: the recorded timestamp
* has millisecond resolution, so the reconstructed arrival times are quantised
* to 1 ms. Device-clock timestamps inside the payloads keep their full 3.9 µs
* resolution, and those are what the clock models actually fit against.
*
* Source recordings: https://github.com/DominiqueMakowski/OpenMuse (MIT).
*/
function importOpenMuseRecording(text, options = {}) {
	const writer = new SessionWriter({
		transport: "openmuse-import",
		device: {
			id: "openmuse-recording",
			name: "MuseS (imported)",
			model: "MS-03",
			protocol: "universal",
			...options.device
		},
		...options.subject === void 0 ? {} : { subject: options.subject },
		notes: options.notes ?? "Imported from an OpenMuse text recording (MIT). Arrival times are quantised to 1 ms by the source format.",
		extra: { importedFrom: "openmuse-tsv" }
	});
	if (options.preset !== void 0) writer.describePreset({ requested: options.preset });
	let lineNumber = 0;
	let imported = 0;
	for (const line of text.split("\n")) {
		lineNumber++;
		if (line.trim().length === 0) continue;
		if (options.limit !== void 0 && imported >= options.limit) break;
		const parts = line.split("	");
		if (parts.length < 3) throw new MuseError("MALFORMED_PACKET", `Line ${lineNumber}: expected three tab-separated fields`, { details: { lineNumber } });
		const [timestamp, characteristic, hex] = parts;
		writer.addFrame(characteristic.trim(), parseHex(hex, lineNumber), parseIsoMicros(timestamp, lineNumber));
		imported++;
	}
	if (imported === 0) throw new MuseError("MALFORMED_PACKET", "Recording contained no notifications");
	return writer.finish();
}
function parseIsoMicros(value, lineNumber) {
	const ms = Date.parse(value.trim());
	if (Number.isNaN(ms)) throw new MuseError("MALFORMED_PACKET", `Line ${lineNumber}: "${value}" is not an ISO-8601 timestamp`);
	return ms * MICROSECONDS_PER_MILLISECOND;
}
function parseHex(value, lineNumber) {
	const hex = value.trim();
	if (hex.length % 2 !== 0) throw new MuseError("MALFORMED_PACKET", `Line ${lineNumber}: hex payload has odd length ${hex.length}`);
	const out = new Uint8Array(hex.length / 2);
	for (let i = 0; i < out.length; i++) {
		const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
		if (Number.isNaN(byte)) throw new MuseError("MALFORMED_PACKET", `Line ${lineNumber}: invalid hex at byte ${i}`);
		out[i] = byte;
	}
	return out;
}
//#endregion
export { FRAME_RECORD_HEADER_SIZE, FrameLogWriter, ReplayTransport, SESSION_FORMAT_VERSION, Session, SessionWriter, Sha256, countFrames, crc32, crc32Update, importOpenMuseRecording, parseManifest, readFrameLog, readSession, readZip, replayTransport, sha256Hex, writeZip };

//# sourceMappingURL=index.js.map